import React, { useState } from 'react';
import { Check, X, Zap, Rocket, Crown, Star, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Loading } from '../components/ui/Loading';
import { usePlans, useCurrentPlan, useBilling, useUpgradePlan } from '../hooks/useBilling';
import { OrganizationPlan, PlanInfo } from '../types';

// Feature categories and descriptions
const FEATURE_CATEGORIES = {
    basic: {
        title: 'Temel Özellikler',
        subtitle: 'Tüm planlarda dahil',
        features: [
            { key: 'payment_tracking', name: 'SDK Event Tracking', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'webhook_tracking', name: 'Provider Webhook Tracking', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'risk_detection', name: 'Otomatik Risk Algılama', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'basic_dashboard', name: 'Dashboard, Stats, Trends', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'risk_analysis', name: 'Risk Listesi ve Detayları', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'payment_list', name: 'Ödeme Listesi', plans: ['free', 'basic', 'standard', 'pro'] },
            { key: 'payment_detail', name: 'Ödeme Detay Sayfası', plans: ['free', 'basic', 'standard', 'pro'] },
        ],
    },
    premium: {
        title: 'Premium Özellikler',
        subtitle: 'Standard ve Pro planlarında',
        features: [
            { key: 'csv_export', name: 'CSV Rapor Export', plans: ['standard', 'pro'] },
            { key: 'pdf_export', name: 'PDF Rapor Export', plans: ['standard', 'pro'] },
            { key: 'email_alerts', name: 'E-posta Uyarıları', plans: ['standard', 'pro'] },
            { key: 'ai_explanations', name: 'AI Risk Açıklamaları', plans: ['standard', 'pro'] },
            { key: 'ai_recommendations', name: 'AI Önerileri', plans: ['standard', 'pro'] },
        ],
    },
    pro: {
        title: 'Pro Özellikler',
        subtitle: 'Sadece Pro planda',
        features: [
            { key: 'priority_support', name: 'Öncelikli Destek', plans: ['pro'] },
        ],
    },
};

const Pricing: React.FC = () => {
    const { data: plans, isLoading: plansLoading } = usePlans();
    const { data: currentPlan, isLoading: currentLoading } = useCurrentPlan();
    const { data: billing } = useBilling();
    const upgradeMutation = useUpgradePlan();

    const [selectedPlan, setSelectedPlan] = useState<OrganizationPlan | null>(null);

    if (plansLoading || currentLoading) {
        return <Loading fullScreen message="Loading pricing plans..." />;
    }

    if (!plans) {
        return (
            <div className="p-6">
                <div className="text-center text-gray-500">Failed to load pricing information</div>
            </div>
        );
    }

    const handleUpgrade = (planCode: OrganizationPlan) => {
        if (planCode === currentPlan?.current_plan) {
            return; // Already on this plan
        }

        setSelectedPlan(planCode);
        upgradeMutation.mutate({ plan: planCode });
    };

    const getPlanIcon = (planCode: OrganizationPlan) => {
        switch (planCode) {
            case 'free':
                return <Zap className="w-6 h-6" />;
            case 'basic':
                return <Rocket className="w-6 h-6" />;
            case 'standard':
                return <Star className="w-6 h-6" />;
            case 'pro':
                return <Crown className="w-6 h-6" />;
            default:
                return <Zap className="w-6 h-6" />;
        }
    };

    const getPlanColor = (planCode: OrganizationPlan) => {
        switch (planCode) {
            case 'free':
                return 'text-gray-600';
            case 'basic':
                return 'text-blue-600';
            case 'standard':
                return 'text-green-600';
            case 'pro':
                return 'text-purple-600';
            default:
                return 'text-gray-600';
        }
    };

    const isCurrentPlan = (planCode: OrganizationPlan) => {
        return planCode === currentPlan?.current_plan;
    };

    const formatNumber = (num: number): string => {
        if (num === 0 || num >= 1000000) return 'Unlimited';
        return num.toLocaleString();
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-3">
                    Choose Your Plan
                </h1>
                <p className="text-xl text-gray-600">
                    Scale your payment monitoring with the right plan for your business
                </p>
                {currentPlan && (
                    <p className="mt-3 text-sm text-gray-500">
                        Currently on: <span className="font-semibold">{currentPlan.plan_name}</span>
                    </p>
                )}
            </div>

            {/* Current Usage (if available) */}
            {billing?.usage_this_period && (
                <Card className="bg-blue-50 border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-900">Current Period Usage</p>
                            <p className="mt-1 text-2xl font-bold text-blue-700">
                                {billing.usage_this_period.payments.toLocaleString()} / {billing.usage_this_period.limit.toLocaleString()}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                                {billing.usage_this_period.percentage.toFixed(1)}% used
                            </p>
                        </div>
                        <TrendingUp className="w-12 h-12 text-blue-400" />
                    </div>
                </Card>
            )}

            {/* Plans Grid */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Hızlı Karşılaştırma</h2>
                <p className="text-gray-600 mb-6 text-center">Size en uygun planı seçin</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {plans.map((plan: PlanInfo) => {
                    const isCurrent = isCurrentPlan(plan.plan);
                    const isHighlighted = plan.highlighted;
                    const isUpgrading = selectedPlan === plan.plan && upgradeMutation.isPending;

                    return (
                        <Card
                            key={plan.plan}
                            className={`relative ${isHighlighted
                                ? 'ring-2 ring-purple-500 shadow-xl scale-105'
                                : ''
                                } ${isCurrent ? 'bg-gray-50' : ''}`}
                        >
                            {isHighlighted && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                        {plan.cta}
                                    </span>
                                </div>
                            )}

                            {isCurrent && (
                                <div className="absolute top-4 right-4">
                                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                                        Current
                                    </span>
                                </div>
                            )}

                            <div className="space-y-4">
                                {/* Plan Header */}
                                <div>
                                    <div className={`inline-flex p-3 rounded-lg bg-gray-100 ${getPlanColor(plan.plan)}`}>
                                        {getPlanIcon(plan.plan)}
                                    </div>
                                    <div className="mt-3">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                            {plan.badge}
                                        </p>
                                        <h3 className="text-2xl font-bold text-gray-900 mt-1">
                                            {plan.name}
                                        </h3>
                                    </div>
                                </div>

                                {/* Pricing */}
                                <div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-bold text-gray-900">
                                            ${plan.price}
                                        </span>
                                        {plan.price > 0 && (
                                            <span className="text-gray-500">/{plan.period}</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                                </div>

                                {/* Target Audience */}
                                <p className="text-xs text-gray-500 italic">{plan.target}</p>

                                {/* CTA Button */}
                                <button
                                    onClick={() => handleUpgrade(plan.plan)}
                                    disabled={isCurrent || isUpgrading || plan.plan === 'free'}
                                    className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${isCurrent
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : isHighlighted
                                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                                            : 'bg-gray-900 text-white hover:bg-gray-800'
                                        } disabled:opacity-50`}
                                >
                                    {isUpgrading
                                        ? 'Upgrading...'
                                        : isCurrent
                                            ? 'Current Plan'
                                            : plan.plan === 'free'
                                                ? 'Get Started Free'
                                                : `Upgrade to ${plan.name}`}
                                </button>

                                {/* Key Features */}
                                <div className="border-t border-gray-200 pt-4">
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                        Limitler
                                    </p>
                                    <ul className="space-y-2">
                                        <li className="flex items-start gap-2 text-sm text-gray-700">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>{formatNumber(plan.payment_limit)} payments/ay</span>
                                        </li>
                                        <li className="flex items-start gap-2 text-sm text-gray-700">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>{plan.retention_days} gün veri saklama</span>
                                        </li>
                                        <li className="flex items-start gap-2 text-sm text-gray-700">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>{plan.team_members} takım üyesi</span>
                                        </li>
                                        <li className="flex items-start gap-2 text-sm text-gray-700">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>
                                                {plan.plan === 'free' ? '2' : plan.plan === 'basic' ? '3' : plan.plan === 'standard' ? '5' : '10'} provider
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2 text-sm text-gray-700">
                                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                            <span>Sınırsız API requests</span>
                                        </li>
                                    </ul>
                                </div>

                                {/* All Features */}
                                {plan.features && plan.features.length > 0 && (
                                    <div className="border-t border-gray-200 pt-4">
                                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                            Özellikler
                                        </p>
                                        <ul className="space-y-1.5">
                                            {plan.plan === 'free' || plan.plan === 'basic' ? (
                                                <>
                                                    <li className="flex items-start gap-2 text-xs text-gray-600">
                                                        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                                        <span>Tüm temel özellikler</span>
                                                    </li>
                                                </>
                                            ) : plan.plan === 'standard' ? (
                                                <>
                                                    <li className="flex items-start gap-2 text-xs text-gray-600">
                                                        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                                        <span>Tüm temel özellikler</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-green-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                                        <span>CSV/PDF Export</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-green-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                                        <span>E-posta Uyarıları</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-green-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                                        <span>AI Risk Açıklamaları</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-green-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                                        <span>AI Önerileri</span>
                                                    </li>
                                                </>
                                            ) : (
                                                <>
                                                    <li className="flex items-start gap-2 text-xs text-gray-600">
                                                        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                                        <span>Tüm Standard özellikler</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-purple-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                                                        <span>Öncelikli Destek</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-purple-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                                                        <span>365 gün veri saklama</span>
                                                    </li>
                                                    <li className="flex items-start gap-2 text-xs text-purple-700 font-medium">
                                                        <Check className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                                                        <span>75K payment/ay</span>
                                                    </li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>
            </div>

            {/* Detailed Comparison Table */}
            <Card>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    Detaylı Plan Karşılaştırması
                </h2>
                
                {/* Limits Table */}
                <div className="overflow-x-auto mb-8">
                    <table className="w-full table-fixed">
                        <colgroup>
                            <col className="w-[30%]" />
                            <col className="w-[17.5%]" />
                            <col className="w-[17.5%]" />
                            <col className="w-[17.5%]" />
                            <col className="w-[17.5%]" />
                        </colgroup>
                        <thead>
                            <tr className="border-b-2 border-gray-300">
                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Fiyatlandırma & Limitler</th>
                                <th className="text-center py-3 px-4 font-semibold text-gray-700">FREE</th>
                                <th className="text-center py-3 px-4 font-semibold text-blue-700">BASIC</th>
                                <th className="text-center py-3 px-4 font-semibold text-green-700 bg-green-50">STANDARD</th>
                                <th className="text-center py-3 px-4 font-semibold text-purple-700">PRO</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Fiyat</td>
                                <td className="py-3 px-4 text-center text-gray-900">$0</td>
                                <td className="py-3 px-4 text-center text-gray-900">$19/ay</td>
                                <td className="py-3 px-4 text-center text-gray-900 bg-green-50">$49/ay</td>
                                <td className="py-3 px-4 text-center text-gray-900">$99/ay</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Süre</td>
                                <td className="py-3 px-4 text-center text-gray-600">Sınırsız</td>
                                <td className="py-3 px-4 text-center text-gray-600">Aylık</td>
                                <td className="py-3 px-4 text-center text-gray-600 bg-green-50">Aylık</td>
                                <td className="py-3 px-4 text-center text-gray-600">Aylık</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Payment Limit</td>
                                <td className="py-3 px-4 text-center text-gray-900">1,000/ay</td>
                                <td className="py-3 px-4 text-center text-gray-900">5,000/ay</td>
                                <td className="py-3 px-4 text-center text-gray-900 bg-green-50 font-semibold">20,000/ay</td>
                                <td className="py-3 px-4 text-center text-gray-900">75,000/ay</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Data Retention</td>
                                <td className="py-3 px-4 text-center text-gray-900">7 gün</td>
                                <td className="py-3 px-4 text-center text-gray-900">30 gün</td>
                                <td className="py-3 px-4 text-center text-gray-900 bg-green-50 font-semibold">90 gün</td>
                                <td className="py-3 px-4 text-center text-gray-900">365 gün</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Team Members</td>
                                <td className="py-3 px-4 text-center text-gray-900">1</td>
                                <td className="py-3 px-4 text-center text-gray-900">2</td>
                                <td className="py-3 px-4 text-center text-gray-900 bg-green-50">5</td>
                                <td className="py-3 px-4 text-center text-gray-900">10</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">Providers</td>
                                <td className="py-3 px-4 text-center text-gray-900">2</td>
                                <td className="py-3 px-4 text-center text-gray-900">3</td>
                                <td className="py-3 px-4 text-center text-gray-900 bg-green-50">5</td>
                                <td className="py-3 px-4 text-center text-gray-900">10</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                                <td className="py-3 px-4 font-medium text-gray-700">API Requests</td>
                                <td className="py-3 px-4 text-center text-gray-600">Sınırsız</td>
                                <td className="py-3 px-4 text-center text-gray-600">Sınırsız</td>
                                <td className="py-3 px-4 text-center text-gray-600 bg-green-50">Sınırsız</td>
                                <td className="py-3 px-4 text-center text-gray-600">Sınırsız</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Features Table */}
                <div className="space-y-8">
                    {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                        <div key={categoryKey}>
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">{category.title}</h3>
                                <p className="text-sm text-gray-600">{category.subtitle}</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full table-fixed">
                                    <colgroup>
                                        <col className="w-[40%]" />
                                        <col className="w-[15%]" />
                                        <col className="w-[15%]" />
                                        <col className="w-[15%]" />
                                        <col className="w-[15%]" />
                                    </colgroup>
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-2 px-4 text-sm font-medium text-gray-700">Özellik</th>
                                            <th className="text-center py-2 px-4 text-sm font-medium text-gray-700">FREE</th>
                                            <th className="text-center py-2 px-4 text-sm font-medium text-blue-700">BASIC</th>
                                            <th className="text-center py-2 px-4 text-sm font-medium text-green-700 bg-green-50">STANDARD</th>
                                            <th className="text-center py-2 px-4 text-sm font-medium text-purple-700">PRO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {category.features.map((feature) => (
                                            <tr key={feature.key} className="border-b border-gray-100">
                                                <td className="py-2 px-4 text-sm text-gray-700">{feature.name}</td>
                                                <td className="py-2 px-4 text-center">
                                                    {feature.plans.includes('free') ? (
                                                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-red-400 mx-auto" />
                                                    )}
                                                </td>
                                                <td className="py-2 px-4 text-center">
                                                    {feature.plans.includes('basic') ? (
                                                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-red-400 mx-auto" />
                                                    )}
                                                </td>
                                                <td className="py-2 px-4 text-center bg-green-50">
                                                    {feature.plans.includes('standard') ? (
                                                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-red-400 mx-auto" />
                                                    )}
                                                </td>
                                                <td className="py-2 px-4 text-center">
                                                    {feature.plans.includes('pro') ? (
                                                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-red-400 mx-auto" />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* FAQ or Contact Section */}
            <Card className="bg-gray-50">
                <div className="text-center py-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Need a custom plan?
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Contact our sales team for enterprise pricing and custom features.
                    </p>
                    <a
                        href="mailto:sales@silentloss.io"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Contact Sales
                    </a>
                </div>
            </Card>
        </div>
    );
};

export default Pricing;
